import { AccountPermission } from 'modules/permissions/constants';
import { UserLabel, IUserLabelProps } from 'uiKit/UserLabel';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAccountStatusTransition } from 'domains/account/hooks/useAccountStatusTransation';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useChargingModel } from 'modules/billing/hooks/useChargingModel';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useMyBundlesStatus } from 'modules/billing/hooks/useMyBundlesStatus';
import { useReferrer } from 'modules/referralProgram/hooks/useReferrer';

import { PublicBadge } from '../PublicBadge';

interface IAccountStatusProps {
  className?: string;
  keepSuffix?: boolean;
  size?: IUserLabelProps['size'];
  suffix?: string;
}

export const AccountStatus = ({
  className,
  keepSuffix = false,
  size = 'medium',
  suffix,
}: IAccountStatusProps) => {
  const {
    initialLoading: accountStatusInitalLoading,
    isEnterprise,
    isPremium,
  } = useAccountStatus();
  const chargingModel = useChargingModel();
  const { isLoggedIn, isLoggingIn } = useAuth();

  const { hasAccountStatusTransition } = useAccountStatusTransition();

  const { isLoading: isReferrerLoading } = useReferrer({
    skipFetching: !isLoggedIn,
  });

  const { data: myBundlesStatus, isLoading } = useMyBundlesStatus();
  const myBundlesStatusLoading = isLoading && myBundlesStatus === undefined;

  const hasAccessToAccountStatus = useCheckAccountPermission({
    permission: AccountPermission.AccountStatus,
  });

  if (!isLoggedIn) {
    return <PublicBadge className={className} />;
  }

  if (!hasAccessToAccountStatus && !keepSuffix) {
    return null;
  }

  const isStatusDataLoading =
    isLoggingIn ||
    accountStatusInitalLoading ||
    isReferrerLoading ||
    myBundlesStatusLoading;

  return (
    <UserLabel
      className={className}
      hasEnterpriseStatus={isEnterprise}
      hasPremium={isPremium || keepSuffix}
      hasStatusTransition={hasAccountStatusTransition}
      isLoading={isStatusDataLoading}
      isPromo={chargingModel?.type === 'promo'}
      keepSuffix={keepSuffix}
      size={size}
      suffix={isPremium || keepSuffix ? suffix : undefined}
    />
  );
};
